import { useState } from "react";
import { SAUNA_ENQUIRY_OPTIONS } from "@/data/sauna-enquiry-options";
import { ArrowRight, ChevronDown, Phone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { trackEvent } from "@/hooks/use-analytics";

const SERVICES = ["AC Installation", "AC Repair", "AC Maintenance", "Ventilation", "Heating", "Ductwork", "BMS & Controls", ...SAUNA_ENQUIRY_OPTIONS];

const labelCls = "block text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-[hsl(215_20%_38%)] mb-1.5";
const fieldCls =
  "w-full h-12 rounded-xl border border-[hsl(213_30%_86%)] bg-[hsl(214_40%_98%)] px-4 text-[0.95rem] text-[hsl(var(--vx-ink))] placeholder:text-[hsl(215_14%_60%)] transition-[border-color,box-shadow,background-color] duration-200 hover:border-[hsl(213_30%_76%)] focus:outline-none focus:bg-white focus:border-[hsl(var(--vx-blue))] focus:shadow-[0_0_0_4px_hsl(var(--vx-blue)/0.12)]";

export function CallbackForm({ defaultService = "" }: { defaultService?: string }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", phone: "", email: "", postcode: "", service: defaultService });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          phone: form.phone,
          email: form.email.trim() || undefined,
          message: `Callback requested. Service: ${form.service || "Not specified"}. Postcode: ${form.postcode || "Not provided"}.`,
          formType: "Callback Request",
          extra: { Service: form.service, Postcode: form.postcode },
        }),
      });
      if (!res.ok) throw new Error("Failed");
      if (form.service.startsWith("Sauna")) trackEvent("sauna_form_success", "Callback request");
      toast({ title: "Callback requested!", description: "We'll call you back shortly." });
      setForm({ name: "", phone: "", email: "", postcode: "", service: defaultService });
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or call 020 8087 0906.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="relative overflow-hidden rounded-[1.5rem] bg-[hsl(214_45%_99%)] shadow-[0_40px_80px_-30px_hsl(220_52%_9%/0.65)] ring-1 ring-white/40"
      data-testid="form-callback"
    >
      <div aria-hidden="true" className="h-1 w-full bg-gradient-to-r from-[hsl(var(--vx-blue))] via-[hsl(var(--vx-frost))] to-[hsl(var(--vx-ember))]" />
      <div className="p-5 sm:p-7">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div className="min-w-0">
            <p className="vx-eyebrow text-[hsl(var(--vx-blue))] mb-1.5 !text-[0.68rem]">{defaultService ? "Discuss your sauna project" : "Free quote in 24h"}</p>
            <h2 className="font-display text-[1.6rem] leading-tight font-bold text-[hsl(var(--vx-ink))] tracking-tight">Request a callback</h2>
          </div>
          <span className="grid place-items-center w-11 h-11 rounded-2xl bg-[hsl(var(--vx-ember)/0.12)] text-[hsl(var(--vx-ember))] ring-1 ring-[hsl(var(--vx-ember)/0.25)] shrink-0" aria-hidden="true">
            <Phone className="w-5 h-5" />
          </span>
        </div>

        {defaultService && (
          <p className="mb-4 rounded-lg bg-[hsl(var(--vx-mist)/0.6)] px-3 py-2 text-xs leading-relaxed text-slate-600">
            We use your details to respond to this enquiry, not to sign you up to marketing. Contact info@vertexclimate.co.uk with privacy questions.
          </p>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-3.5">
          <div>
            <label className={labelCls} htmlFor="cb-name">Your name <span className="text-[hsl(var(--vx-ember))]" aria-hidden="true">*</span></label>
            <input id="cb-name" className={fieldCls} required autoComplete="name" placeholder="Jane Smith" value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))} data-testid="input-callback-name" />
          </div>
          <div>
            <label className={labelCls} htmlFor="cb-phone">Phone number <span className="text-[hsl(var(--vx-ember))]" aria-hidden="true">*</span></label>
            <input id="cb-phone" type="tel" inputMode="tel" className={fieldCls} required autoComplete="tel" placeholder="07..." value={form.phone}
              onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} data-testid="input-callback-phone" />
          </div>
          <div>
            <label className={`${labelCls} sm:min-h-8`} htmlFor="cb-email">Email address <span className="text-[hsl(var(--vx-ember))]" aria-hidden="true">*</span></label>
            <input id="cb-email" name="email" type="email" inputMode="email" required className={fieldCls} autoComplete="email" placeholder="you@example.com" value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))} data-testid="input-callback-email" />
          </div>
          <div>
            <label className={`${labelCls} sm:min-h-8`} htmlFor="cb-postcode">Postcode <span className="normal-case tracking-normal font-normal text-slate-400">(optional)</span></label>
            <input id="cb-postcode" className={fieldCls} autoComplete="postal-code" placeholder="e.g. SW1A 1AA" value={form.postcode}
              onChange={e => setForm(p => ({ ...p, postcode: e.target.value }))} data-testid="input-callback-postcode" />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls} htmlFor="cb-service">Service</label>
            <div className="relative">
              <select id="cb-service" className={`${fieldCls} appearance-none pr-10 cursor-pointer`} value={form.service}
                onChange={e => setForm(p => ({ ...p, service: e.target.value }))} data-testid="select-callback-service">
                <option value="">Select service</option>
                {SERVICES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" aria-hidden="true" />
            </div>
          </div>
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="group mt-5 w-full h-[3.25rem] rounded-xl bg-[hsl(var(--vx-ink))] text-white font-semibold flex items-center justify-center gap-2 shadow-[0_12px_24px_-12px_hsl(220_52%_9%/0.7)] transition-[background-color,transform] duration-200 hover:bg-[hsl(var(--vx-blue))] active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed motion-reduce:transition-none"
          data-testid="button-callback-submit"
        >
          {submitting ? "Sending..." : "Get free quote"}
          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1 motion-reduce:transition-none" aria-hidden="true" />
        </button>

        <div className="mt-4 flex items-center gap-3 text-xs text-slate-500">
          <span className="h-px flex-1 bg-[hsl(var(--vx-mist))]" aria-hidden="true" />
          <p>Prefer to talk? <a href="tel:02080870906" className="font-semibold text-[hsl(var(--vx-ink))] underline-offset-4 hover:underline">020 8087 0906</a></p>
          <span className="h-px flex-1 bg-[hsl(var(--vx-mist))]" aria-hidden="true" />
        </div>
      </div>
    </form>
  );
}
